import React, { Component } from "react";
import PropTypes from "prop-types";
import { JobService } from "@services";
import utils from "@utils";
import { CUSTOM_PAST_STATIC_RAGES, CUSTOM_FUTURE_STATIC_RAGES } from "@constants";
import { DateRange } from "@components/common";
import { JOB_INVOICED_TYPES } from "@constants/index";
import { Modal, Switch, Tooltip } from "../../../../../components";
import "./AddJobsModal.scss";

class AddJobsModal extends Component {
	constructor(props) {
		super(props);
		this.initialState = {
			loading: false,
			client: {},
			onSave: () => {},
			showModal: false,
			jobEventList: [],
			selectedJobEvents: [],
			selectAll: false,
			dateRange: 30 * 24 * 60 * 60 * 1000,
			onlyJobsWithExcludeOfficerRate: false,
			disabledJobIds: [],
			excludeOfficerRateToggleEnabled: false,
			showCustomCalendar: false,
			selectedStartDateRange: null,
			selectedEndDateRange: null,
			showMixedInvoiceTypeToolTip: false,
		};

		this.state = { ...this.initialState };

		this.tableRef = React.createRef();
	}

	openModal = settings => {
		const { addCustomDateEnabled, user } = this.props;
		const { dateRange } = this.state;

		let formatStartDate = null;
		let formatEndDate = null;

		if (addCustomDateEnabled) {
			let startDate = new Date(new Date().getTime() - dateRange);
			let endDate = new Date();

			if (settings.client.isEstimate) {
				startDate = new Date();
				endDate = new Date(new Date().getTime() + dateRange);
			}

			formatStartDate = utils.formatDate(startDate, "en-US", user.TimeZoneId, "numeric");
			formatEndDate = utils.formatDate(endDate, "en-US", user.TimeZoneId, "numeric");
		}

		this.setState(
			{
				...settings,
				showModal: true,
				selectedStartDateRange: formatStartDate,
				selectedEndDateRange: formatEndDate,
			},
			() => {
				if (settings.client.id) this.getNonRKPayJobs();
			},
		);
	};

	handleModalClose = () => {
		this.setState(this.initialState);
	};

	/** Retrieves a list of non-RKPay invoices per job */
	getNonRKPayJobs = () => {
		const { user, invoiceId, jobInvoicedTypeId } = this.props;
		const {
			dateRange,
			client,
			showModal,
			onlyJobsWithExcludeOfficerRate,
			selectedStartDateRange,
			selectedEndDateRange,
		} = this.state;

		let startDate = selectedStartDateRange || new Date(new Date().getTime() - dateRange);
		let endDate = selectedEndDateRange || new Date();

		if (client.isEstimate) {
			startDate = selectedStartDateRange || new Date();
			endDate = selectedEndDateRange || new Date(new Date().getTime() + dateRange);
		}

		this.setState({ loading: true });
		const params = {
			clientId: client.id,
			startDate:
				selectedStartDateRange ||
				utils.formatDate(startDate, "en-US", user.TimeZoneId, "numeric"),
			endDate:
				selectedEndDateRange ||
				utils.formatDate(endDate, "en-US", user.TimeZoneId, "numeric"),
			workedJobs: !client.isEstimate,
			nonRkPayInvoiceId: invoiceId,
			onlyJobsWithExcludeOfficerRate,
			jobInvoicedTypeId,
		};

		JobService.getNonRKPayInvoices(params)
			.then(({ data }) => {
				const hasJobsWithExcludeOfficerRate = data.some(
					job => job.ExcludeOfficerRate === true,
				);

				this.setState({
					jobEventList: data,
					selectedJobEvents: [],
					selectAll: false,
					disabledJobIds: [],
					excludeOfficerRateToggleEnabled: hasJobsWithExcludeOfficerRate,
				});
				if (showModal) this.tableRef.current.scrollIntoView({ behavior: "smooth" });
			})
			.catch(error => {
				utils.notify({
					type: "error",
					message: error.Message || "Unable to retrieve Non RKPay invoices",
					timeOut: 1500,
				});
			})
			.finally(() =>
				this.setState({
					loading: false,
					showMixedInvoiceTypeToolTip: !this.checkJobsAreAllTheSameInvoicedType(),
				}
			));
	};

	/** Selects all invoice from the list */
	selectAllInvoices = () => {
		const { jobEventList, selectAll } = this.state;
		const selected = [];

		if (selectAll) {
			this.setState({
				selectedJobEvents: [],
				selectAll: false,
			});
			return;
		}

		const resExcludeOfficerRate = this.checkJobsAreAllOfficerPayOrExludeOfficerPay();
		const resInvoiceType = this.checkJobsAreAllTheSameInvoicedType();

		if (!resExcludeOfficerRate) {
			utils.notify({
				type: "error",
				message: "You need to select jobs that are all Officer Pay or all Exclude Officer Pay",
				timeOut: 1500,
			});
			return;
		}

		if (!resInvoiceType) {
			utils.notify({
				type: "error",
				message: "You need to select jobs that are all Officer Invoiced or all Agency Invoiced",
				timeOut: 1500,
			});
			return;
		}

		jobEventList.map(item => selected.push(item.Id));

		this.setState({
			selectedJobEvents: selected,
			selectAll: true,
		});
	};

	/**
	 * Selects an individual invoice from the list
	 * @param {Number} invoiceId The unique id of he invoice being selected
	 */
	selectIndividualInvoices = invoiceId => () => {
		const { selectedJobEvents, jobEventList } = this.state;
		const jobIdsToDisable = this.getJobIdsToDisableBasedOnInvoiceId(invoiceId);

		if (
			!selectedJobEvents.includes(invoiceId) &&
			this.checkSelectedJobsAreAllOfficerPayOrExludeOfficerPay() &&
			this.checkSelectedJobsAreAllTheSameInvoiceType()
		) {
			const nextSelected = [...selectedJobEvents, invoiceId];
			this.setState({
				selectedJobEvents: nextSelected,
				selectAll: nextSelected.length === jobEventList.length,
				disabledJobIds: jobIdsToDisable,
			});
		} else {
			this.setState({
				selectedJobEvents: selectedJobEvents.filter(id => id !== invoiceId),
				selectAll: false,
				disabledJobIds: jobIdsToDisable,
			});
		}
	};

	handleDateChange = () => e => {
		const { value } = e.target;

		this.setState({ dateRange: parseInt(value, 10) * 24 * 60 * 60 * 1000 }, () => {
			this.getNonRKPayJobs();
		});
	};

	handleDateRangeChange = ranges => {
		const { startDate, endDate } = ranges;

		this.setState(
			{
				selectedStartDateRange: startDate,
				selectedEndDateRange: endDate,
			},
			() => {
				this.getNonRKPayJobs();
			},
		);
	};

	handleAddJobs = () => {
		const { selectedJobEvents, onSave } = this.state;

		const resSelectedExcludeOfficerPay = this.checkSelectedJobsAreAllOfficerPayOrExludeOfficerPay();
		const resSelectedInvoiceType = this.checkSelectedJobsAreAllTheSameInvoiceType();

		if (!this.checkLineItemsAreAllOfficerPayOrExludeOfficerPay() || !resSelectedExcludeOfficerPay) {
			utils.notify({
				type: "error",
				message:
					"You can only add jobs that are all Officer Pay or all Exclude Officer Pay",
				timeOut: 1500,
			});
			return;
		}

		if (!this.checkLineItemsAreAllTheSameInvoiceType() || !resSelectedInvoiceType) {
			utils.notify({
				type: "error",
				message:
					"You can only add jobs that are all Officer Invoiced or all Agency Invoiced",
				timeOut: 1500,
			});
			return;
		}

		onSave(selectedJobEvents);
		this.handleModalClose();
	};

	checkLineItemsAreAllOfficerPayOrExludeOfficerPay = () => {
		const { selectedJobEvents, jobEventList } = this.state;
		const { lineItems } = this.props;

		if (lineItems.length > 0) {
			const lineItemsAreOfficerPay = lineItems.every(
				lineItem => lineItem.ExcludeOfficerRate === false,
			);
			const lineItemsAreExcludeOfficerPay = lineItems.every(
				lineItem => lineItem.ExcludeOfficerRate === true,
			);
			const selectedJobs = jobEventList.filter(job => selectedJobEvents.includes(job.Id));
			if (lineItemsAreOfficerPay) {
				return selectedJobs.every(
					job => job.ExcludeOfficerRate === false || job.ExcludeOfficerRate === null,
				);
			}
			if (lineItemsAreExcludeOfficerPay) {
				return selectedJobs.every(job => job.ExcludeOfficerRate === true);
			}
		}
		return true;
	};

	checkLineItemsAreAllTheSameInvoiceType = () => {
		const { selectedJobEvents, jobEventList } = this.state;
		const { lineItems } = this.props;

		if (lineItems.length > 0) {
			const lineItemsAreOfficerInvoiced = lineItems.every(
				lineItem => lineItem.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING,
			);
			const lineItemsAreAgencyInvoiced = lineItems.every(
				lineItem => lineItem.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING,
			);
			const selectedJobs = jobEventList.filter(job => selectedJobEvents.includes(job.Id));
			if (lineItemsAreOfficerInvoiced) {
				return selectedJobs.every(
					job => job.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING,
				);
			}
			if (lineItemsAreAgencyInvoiced) {
				return selectedJobs.every(
					job => job.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING,
				);
			}
		}
		return true;
	}

	checkSelectedJobsAreAllOfficerPayOrExludeOfficerPay = () => {
		const { selectedJobEvents, jobEventList } = this.state;

		const selectedJobs = jobEventList.filter(job => selectedJobEvents.includes(job.Id));
		const isAllOfficerPay = selectedJobs.every(
			job => job.ExcludeOfficerRate === false || job.ExcludeOfficerRate === null,
		);
		const isAllExcludeOfficerPay = selectedJobs.every(job => job.ExcludeOfficerRate === true);

		return (isAllOfficerPay || isAllExcludeOfficerPay);
	};

	checkSelectedJobsAreAllTheSameInvoiceType = () => {
		const { selectedJobEvents, jobEventList } = this.state;

		const selectedJobs = jobEventList.filter(job => selectedJobEvents.includes(job.Id));
		const isAllJobsOfficerInvoiced = selectedJobs.every(
			job => job.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING
		);
		const isAllJobsAgencyInvoiced = selectedJobs.every(
			job => job.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING
		);

		return (isAllJobsOfficerInvoiced || isAllJobsAgencyInvoiced);
	};

	handleExcludeOffierRateToggle = () => {
		this.setState(
			prevState => ({
				onlyJobsWithExcludeOfficerRate: !prevState.onlyJobsWithExcludeOfficerRate,
			}),
			this.getNonRKPayJobs,
		);
	};

	getJobIdsToDisableBasedOnInvoiceId = invoiceId => {
		const { selectedJobEvents, jobEventList } = this.state;

		const invoice = jobEventList.find(i => i.Id === invoiceId);
		if (!invoice) return [];

		if (selectedJobEvents.includes(invoiceId) && selectedJobEvents.length === 1) return [];

		const filterJobIdsbasedOnExcludeOfficerRateList = this.filterJobIdsBasedOnExlcudeOfficerRateBasedOnInvoice(invoice);
		const filterJobIdsbasedOnJobEventInvoicedTypeId = this.filterJobIdsBasedOnJobEventInvoicedTypeId(invoice);

		// concat both lists to get the final list of job ids to disable, filtering out duplicates
		return [
			...new Set([
				...filterJobIdsbasedOnExcludeOfficerRateList,
				...filterJobIdsbasedOnJobEventInvoicedTypeId,
			]),
		];
	};

	filterJobIdsBasedOnExlcudeOfficerRateBasedOnInvoice = invoice => {
		const { jobEventList } = this.state;
		let jobsIdstoDisable = [];

		if (invoice.ExcludeOfficerRate) {
			jobsIdstoDisable = jobEventList.filter(
				job => job.ExcludeOfficerRate === false || job.ExcludeOfficerRate === null,
			);
		} else {
			jobsIdstoDisable = jobEventList.filter(job => job.ExcludeOfficerRate === true);
		}

		return jobsIdstoDisable.map(j => j.Id);
	};

	filterJobIdsBasedOnJobEventInvoicedTypeId = invoice => {
		const { jobEventList } = this.state;
		let jobsIdstoDisable = [];

		if (invoice.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING) {
			jobsIdstoDisable = jobEventList.filter(
				job => job.JobEventInvoicedTypeId !== JOB_INVOICED_TYPES.OFFICER_INVOICING,
			);
		} else {
			jobsIdstoDisable = jobEventList.filter(
				job => job.JobEventInvoicedTypeId !== JOB_INVOICED_TYPES.AGENCY_INVOICING || job.ExcludeOfficerRate === true,
			);
		}

		return jobsIdstoDisable.map(j => j.Id);
	}

	checkJobsAreAllOfficerPayOrExludeOfficerPay = () => {
		const { jobEventList } = this.state;

		const isAllJobsOfficerPay = jobEventList.every(
			job => job.ExcludeOfficerRate === false || job.ExcludeOfficerRate === null,
		);
		const isAllJobsExcludeOfficerRate = jobEventList.every(
			job => job.ExcludeOfficerRate === true,
		);

		return (isAllJobsOfficerPay || isAllJobsExcludeOfficerRate);
	};

	checkJobsAreAllTheSameInvoicedType = () => {
		const { jobEventList } = this.state;

		const isAllJobsOfficerInvoiced = jobEventList.every(
			job => job.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING
		);

		const isAllJobsAgencyInvoiced = jobEventList.every(
			job => job.JobEventInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING
		);

		return (isAllJobsOfficerInvoiced || isAllJobsAgencyInvoiced);
	}

	onCustomDateRangeChange = () => {
		this.setState(prevState => ({
			showCustomCalendar: !prevState.showCustomCalendar,
		}));
	};

	onRemoveCustomDate = () => {
		this.setState({
			jobEventList: [],
			selectedJobEvents: [],
			selectAll: false,
			disabledJobIds: [],
			excludeOfficerRateToggleEnabled: false,
			selectedStartDateRange: null,
			selectedEndDateRange: null,
		});
	};

	render() {
		const { lang } = global;
		const { user, addCustomDateEnabled } = this.props;
		const {
			loading,
			showModal,
			jobEventList,
			selectedJobEvents,
			selectAll,
			onlyJobsWithExcludeOfficerRate,
			disabledJobIds,
			client,
			excludeOfficerRateToggleEnabled,
			showCustomCalendar,
			selectedStartDateRange,
			selectedEndDateRange,
			showMixedInvoiceTypeToolTip,
		} = this.state;
		const period = client.isEstimate ? "Next" : "Past";
		const dateRangeString = lang.t("Date Range");
		const dateRangeActive = selectedStartDateRange && selectedEndDateRange;

		const formatedDateRange =
			selectedStartDateRange && selectedEndDateRange
				? `${dateRangeString} : ${utils.formatDate(selectedStartDateRange, "en-US", undefined, "2-digit")}
			- ${utils.formatDate(selectedEndDateRange, "en-US", undefined, "2-digit")}`
				: lang.t("Select Date Range");

		return (
			<>
				<Modal
					show={showModal}
					onHide={() => this.handleModalClose()}
					id="add-jobs-modal"
					enforceFocus={false}
				>
					{/* Modal Header */}
					<Modal.Header
						className="container-fluid add-jobs-header"
						closeButton
					>
						<div className="row">
							<div className="col-xs-5 col-sm-7 job-name">
								<h4
									className="text-left"
									title="Client Jobs"
								>
									{client.name}
								</h4>
								<If condition={addCustomDateEnabled}>
									<button
										type="button"
										className="btn-link text-left header-button"
										onClick={this.onCustomDateRangeChange}
									>
										{formatedDateRange}
									</button>
									<If condition={dateRangeActive}>
										<button
											type="button"
											aria-label="Remove date"
											className="btn btn-link"
											onClick={this.onRemoveCustomDate}
										>
											<i className="glyphicon glyphicon-remove text-danger add-jobs-remove-button" />
										</button>
									</If>
								</If>
							</div>
							<div className="col-xs-6 col-sm-4">
								<If condition={!addCustomDateEnabled}>
									<select
										id="jobDisplayDate"
										name="jobDisplayDate"
										className="form-control"
										onChange={this.handleDateChange()}
									>
										<option value={30}>
											{lang.t("dynamic.Thirty Day Interval", { period })}
										</option>
										<option value={90}>
											{lang.t("dynamic.Ninety Day Interval", { period })}
										</option>
										<option value={365}>
											{lang.t("dynamic.Yearly interval", { period })}
										</option>
									</select>
								</If>
							</div>
						</div>
						<If condition={excludeOfficerRateToggleEnabled}>
							<div className="row toggle-section">
								<div className="col-xs-12 toggle-group">
									<Switch
										on={onlyJobsWithExcludeOfficerRate}
										onClick={this.handleExcludeOffierRateToggle}
									/>
									<Tooltip
										content={lang.t(
											"Exclude Officer Pay Add Jobs Modal Message",
										)}
									>
										<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
									</Tooltip>
									<br />
									<small>{lang.t("Jobs with Exclude Officer Pay")}</small>
								</div>
							</div>
							<div className="row toggle-section">
								<div className="col-xs-12">
									<spna>
										{lang.t(
											"All jobs that have opted to exclude officer pay on the invoice must be grouped together_",
										)}
									</spna>
								</div>
							</div>
						</If>
						<If condition={showMixedInvoiceTypeToolTip}>
							<div>
								<Tooltip content={lang.t("Officer and Agency Jobs cannot be added to a single Estimate/Invoice")}>
									<i className="rkf rkf-question-solid tooltip-icon" />
								</Tooltip>
							</div>
						</If>
					</Modal.Header>
					{/* Modal Header */}

					{/* Modal Body */}
					<Modal.Body className="container-fluid">
						<div className="job-table-header row">
							<div className="col-xs-12 col-sm-1">
								<input
									type="checkbox"
									id="selectAllJobs"
									name="selectAllJobs"
									checked={selectAll}
									onChange={() => this.selectAllInvoices()}
								/>
								<label
									htmlFor="selectAllJobs"
									className="visible-xs-inline-block"
								>
									{lang.t("Select All")}
								</label>
							</div>
							<div className="col-sm-11 hidden-xs">
								<div className="row">
									<div className="col-sm-2">{lang.t("Job ID")}</div>
									<div className="col-sm-4">{lang.t("Job")}</div>
									<div className="col-sm-3">{lang.t("Start")}</div>
									<div className="col-sm-3">{lang.t("End")}</div>
								</div>
							</div>
						</div>
						<div className="job-table-body container-fluid">
							<div ref={this.tableRef} />
							<Choose>
								<When condition={!loading && jobEventList.length === 0}>
									<div className="row non-job text-center text-muted">
										<div className="col-xs-12">No results</div>
									</div>
								</When>
								<When condition={loading}>
									<div className="row non-job text-center">
										<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
									</div>
								</When>
							</Choose>
							<For
								each="invoice"
								of={jobEventList}
								index="index"
							>
								<div
									className={`row v-align-sm job-table-row ${selectedJobEvents.includes(invoice.Id) ? "selected" : ""} ${disabledJobIds.includes(invoice.Id) ? "disabled" : ""}`}
									key={`${invoice.ClientName}_${invoice.Id}`}
									onClick={this.selectIndividualInvoices(invoice.Id)}
									onKeyDown={this.selectIndividualInvoices(invoice.Id)}
									role="button"
									tabIndex={0}
									aria-disabled={disabledJobIds.includes(invoice.Id)}
								>
									<div className="col-xs-2 col-sm-1 job-checkbox">
										<input
											type="checkbox"
											id={`selectIndividualJobs_${invoice.Id}`}
											name="selectIndividualJobs"
											checked={selectedJobEvents.includes(invoice.Id)}
											onChange={() => {}}
											disabled={disabledJobIds.includes(invoice.Id)}
											data-test-id={`addJobsModal__jobEvent-check-${invoice.Id}`}
										/>
									</div>
									<div className="col-xs-10 col-sm-11">
										<div className="row v-align-sm">
											<div className="col-xs-12 col-sm-2 job-id-column">
												<span className="visible-xs-inline-block">
													{`${lang.t("Job ID")}:`}&nbsp;
												</span>
												{invoice.Id}
											</div>
											<div className="col-xs-12 col-sm-4 job-name job-name-column">
												{invoice.Name}
											</div>
											<div className="col-xs-12 col-sm-3 job-date-column">
												<span className="visible-xs-inline-block">
													{lang.t("Start")}:&nbsp;
												</span>
												{utils.formatDate(
													`${invoice.StartDateTime}Z`,
													"en-US",
													user.TimeZoneId,
													"numeric",
												)}
												&nbsp;
												<br className="hidden-xs" />
												{utils.formatTime(
													new Date(`${invoice.StartDateTime}Z`),
													true,
													user.TimeZoneId,
												)}
											</div>
											<div className="col-xs-12 col-sm-3 job-date-column">
												<span className="visible-xs-inline-block ">
													{lang.t("End")}:&nbsp;
												</span>
												{utils.formatDate(
													`${invoice.EndDateTime}Z`,
													"en-US",
													user.TimeZoneId,
													"numeric",
												)}
												&nbsp;
												<br className="hidden-xs" />
												{utils.formatTime(
													new Date(`${invoice.EndDateTime}Z`),
													true,
													user.TimeZoneId,
												)}
											</div>
										</div>
									</div>
								</div>
							</For>
						</div>
					</Modal.Body>
					{/* Modal Body */}

					{/* Modal Footer */}
					<Modal.Footer className="text-center">
						<button
							type="button"
							id="addJobsButton"
							className="btn btn-primary"
							disabled={selectedJobEvents.length === 0}
							onClick={() => this.handleAddJobs()}
							data-test-id="addJobsModal__add-job-button"
						>
							{lang.t("Add Jobs")}
						</button>
					</Modal.Footer>
					{/* Modal Footer */}
				</Modal>
				<DateRange
					show={showCustomCalendar}
					onCancel={() => this.onCustomDateRangeChange()}
					onChange={ranges => this.handleDateRangeChange(ranges)}
					customRanges={
						client.isEstimate ? CUSTOM_FUTURE_STATIC_RAGES : CUSTOM_PAST_STATIC_RAGES
					}
					minDate={
						client.isEstimate
							? new Date()
							: new Date(new Date().setFullYear(new Date().getFullYear() - 3))
					}
					maxDate={
						client.isEstimate
							? new Date(new Date().setFullYear(new Date().getFullYear() + 3))
							: new Date()
					}
					startingDate={selectedStartDateRange}
					endingDate={selectedEndDateRange}
				/>
			</>
		);
	}
}

AddJobsModal.propTypes = {
	user: PropTypes.object,
	invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	lineItems: PropTypes.array,
	addCustomDateEnabled: PropTypes.bool,
	jobInvoicedTypeId: PropTypes.number,
};

AddJobsModal.defaultProps = {
	user: {},
	invoiceId: undefined,
	lineItems: [],
	addCustomDateEnabled: false,
	jobInvoicedTypeId: null
};

export default AddJobsModal;
